// LT-181: Settings -> Integrations page. Lists connected calendars,
// lets user connect a new Google or Microsoft calendar, and disconnect
// an existing one.
// LT-270: per-row type dropdown (user / account) to satisfy HLD AC-2.
// LT-396: CRM summary tile linking out to /settings/integrations/crm.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CalendarType } from '../types/calendar';
import { calendarsAPI } from '../services/calendarsService';
import {
  CRMConnection,
  CRMConnectionStatus,
  asCrmApiError,
  crmSyncAPI,
} from '../services/crmSyncService';
import { APP_NAME } from '../config/region';

const CRM_STATUS_BADGE: Record<CRMConnectionStatus, { label: string; className: string }> = {
  connecting: {
    label: 'Connecting…',
    className: 'bg-amber-500/10 text-amber-300 border-amber-500/40',
  },
  active: {
    label: 'Active',
    className: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40',
  },
  error: {
    label: 'Error',
    className: 'bg-red-500/10 text-red-300 border-red-500/40',
  },
  disconnected: {
    label: 'Disconnected',
    className: 'bg-slate-600/10 text-slate-300 border-slate-500/40',
  },
};

const CRM_PROVIDER_LABEL: Record<CRMConnection['provider'], string> = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  zoho: 'Zoho CRM',
};

const SettingsIntegrationsPage: React.FC = () => {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [typeUpdating, setTypeUpdating] = useState<string | null>(null);
  const [typeNotice, setTypeNotice] = useState<string | null>(null);

  const [crmConnection, setCrmConnection] = useState<CRMConnection | null>(null);
  const [crmLoading, setCrmLoading] = useState(true);
  const [crmForbidden, setCrmForbidden] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await calendarsAPI.list();
      setCalendars(rows);
    } catch (e) {
      setError('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  const loadCrm = async () => {
    setCrmLoading(true);
    try {
      const conn = await crmSyncAPI.getConnection();
      setCrmConnection(conn);
      setCrmForbidden(false);
    } catch (e) {
      const parsed = asCrmApiError(e);
      setCrmForbidden(parsed.isForbidden);
      // Non-admin (403) is expected — don't surface; the tile renders a
      // read-only "ask an admin" hint. Other errors leave the tile in its
      // default not-connected state without breaking the calendars panel.
    } finally {
      setCrmLoading(false);
    }
  };

  useEffect(() => {
    void load();
    void loadCrm();
  }, []);

  const connectProvider = async (provider: 'google' | 'microsoft') => {
    try {
      const start =
        provider === 'google'
          ? calendarsAPI.startGoogleOAuth
          : calendarsAPI.startMicrosoftOAuth;
      const url = (await start()).authorize_url;
      window.location.href = url;
    } catch (e) {
      setError(`Could not start ${provider} OAuth. Check that the app is configured.`);
    }
  };

  const doDisconnect = async (id: string) => {
    try {
      await calendarsAPI.disconnect(id);
      setConfirming(null);
      await load();
    } catch (e) {
      setError('Disconnect failed');
    }
  };

  const changeType = async (cal: Calendar, nextType: CalendarType) => {
    if (nextType === cal.type) return;
    const previousType = cal.type;
    setTypeUpdating(cal.id);
    setTypeNotice(null);
    setError(null);
    // Optimistic update.
    setCalendars((prev) =>
      prev.map((c) => (c.id === cal.id ? { ...c, type: nextType } : c)),
    );
    try {
      const updated = await calendarsAPI.update(cal.id, { type: nextType });
      setCalendars((prev) => prev.map((c) => (c.id === cal.id ? updated : c)));
      setTypeNotice(`${cal.name}: set to ${nextType} calendar.`);
    } catch (e) {
      // Rollback on failure.
      setCalendars((prev) =>
        prev.map((c) => (c.id === cal.id ? { ...c, type: previousType } : c)),
      );
      setError('Failed to update calendar type.');
    } finally {
      setTypeUpdating(null);
    }
  };

  const renderCrmTile = () => {
    if (crmLoading) {
      return <p className="text-gray-400">Loading…</p>;
    }
    const isConnected =
      crmConnection !== null && crmConnection.status !== 'disconnected';
    if (isConnected && crmConnection) {
      const badge = CRM_STATUS_BADGE[crmConnection.status];
      return (
        <div
          data-testid="crm-summary-tile-connected"
          className="flex items-center justify-between gap-4"
        >
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-white text-base font-medium">
                {CRM_PROVIDER_LABEL[crmConnection.provider]}
              </span>
              <span
                className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${badge.className}`}
              >
                {badge.label}
              </span>
            </div>
            {crmConnection.connected_at && (
              <div className="text-sm text-gray-400">
                Connected {new Date(crmConnection.connected_at).toLocaleDateString()}
              </div>
            )}
            {crmConnection.status === 'error' && crmConnection.last_error && (
              <div className="mt-1 text-sm text-red-300">
                {crmConnection.last_error}
              </div>
            )}
          </div>
          <Link
            to="/settings/integrations/crm"
            className="px-4 py-2 border border-white/10 text-gray-200 rounded hover:bg-white/5"
          >
            Manage
          </Link>
        </div>
      );
    }
    return (
      <div
        data-testid="crm-summary-tile-empty"
        className="flex items-center justify-between gap-4"
      >
        <div>
          <p className="text-gray-200">
            Connect HubSpot, Salesforce, or Zoho to sync contacts, accounts,
            and opportunities with {APP_NAME}.
          </p>
          {crmForbidden && (
            <p className="mt-1 text-sm text-gray-400">
              Workspace admin access required to connect a CRM.
            </p>
          )}
        </div>
        {!crmForbidden && (
          <Link
            to="/settings/integrations/crm"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
          >
            Connect a CRM
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto text-gray-200" data-testid="settings-integrations-page">
      <h1 className="text-2xl font-semibold mb-4 text-white">Integrations</h1>
      <section className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-medium mb-3 text-white">Connect a calendar</h2>
        <div className="flex gap-3">
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => connectProvider('google')}
          >
            Connect Google Calendar
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => connectProvider('microsoft')}
          >
            Connect Microsoft Calendar
          </button>
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-medium mb-3 text-white">Connected calendars</h2>
        {loading && <p className="text-gray-400">Loading...</p>}
        {error && <p className="text-red-600" role="alert">{error}</p>}
        {typeNotice && (
          <p className="text-green-400 text-sm mb-2" role="status">{typeNotice}</p>
        )}
        {!loading && calendars.length === 0 && (
          <p className="text-gray-400">No calendars connected yet.</p>
        )}
        {calendars.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-white/10 text-gray-400 uppercase text-xs tracking-wide">
                <th className="py-2">Name</th>
                <th className="py-2">Provider</th>
                <th className="py-2">Type</th>
                <th className="py-2">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {calendars.map((cal) => (
                <tr key={cal.id} className="border-b border-white/10 last:border-b-0 text-gray-200">
                  <td className="py-2">{cal.name}</td>
                  <td className="py-2 capitalize">{cal.provider}</td>
                  <td className="py-2">
                    <select
                      aria-label={`Calendar type for ${cal.name}`}
                      value={cal.type}
                      disabled={typeUpdating === cal.id}
                      onChange={(e) => changeType(cal, e.target.value as CalendarType)}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200 disabled:opacity-50"
                    >
                      <option value="user">User calendar</option>
                      <option value="account">Account calendar</option>
                    </select>
                  </td>
                  <td className="py-2">{cal.is_active ? 'Active' : 'Inactive'}</td>
                  <td className="py-2 text-right">
                    {confirming === cal.id ? (
                      <>
                        <button
                          type="button"
                          className="mr-2 text-red-600 underline"
                          onClick={() => doDisconnect(cal.id)}
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          className="text-gray-400"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="text-red-600"
                        onClick={() => setConfirming(cal.id)}
                      >
                        Disconnect
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section
        data-testid="crm-summary-section"
        className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4"
      >
        <h2 className="text-lg font-medium mb-3 text-white">CRM connections</h2>
        {renderCrmTile()}
      </section>
    </div>
  );
};

export default SettingsIntegrationsPage;
